import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ButtonVariantEnum,
  JxtAlertTypeEnum,
  JxtButton,
  dateNotInPastSuperRefinerFactory,
  startBeforeEndSuperRefinerFactory,
} from '@jooxter/ui';
import JxtAlert from '@jooxter/ui/src/components/JxtAlert';
import { ResourceTypeDtoMetaTypeEnum, UserQuotaRulePeriodEnum, WorkplaceRuleDtoWorkplaceTypeEnum } from '@jooxter/api';
import {
  getDailyQuota,
  getResourceQuotaAlert,
  overflowsQuota,
  useCheckBookingOverlap,
  useOverlapResolution,
} from '@jooxter/bookings';
import { usePermissions } from '@jooxter/permissions';
import { useInvalidateWorkplaceQueries } from '@jooxter/workplaces';
import { useFetchResourceStatus } from '@jooxter/resources';
import {
  createGTMGAEvent,
  fromISO,
  IBookingForm,
  JxtWorkplaceTypeWithoutUnknownEnum,
  toISO,
  useLatest,
} from '@jooxter/utils';
import { JxtAttendeeOption, JxtWorkplaceStrategyLimitContainer } from '../../../../components';
import {
  useFetchCorporationById,
  useFetchCorporationQuota,
  useFetchRemainingBookingsCheck,
  useFetchUser,
  useFetchUserById,
  useFetchUserResourceQuota,
  useInvalidateBookingQueries,
} from '../../../../queries';
import { useBookingSchema } from '../../schemas/useBookingSchema';
import { JxtBookingFormFields } from '../JxtBookingFormFields';
import { useMinMaxAttendees } from '../../hooks/useMinMaxAttendees';
import { useBookingSubmission } from '../../hooks/useBookingSubmission';
import { useKeepBookingDuration } from '../../hooks/useKeepBookingDuration';
import { IJxtBookingFormContent } from '../JxtBookingForm/types';

export const JxtBookingFormContent = ({
  bookingToEdit,
  slotToBookInstantly,
  onSuccess,
  onMoveToNewResource,
  moveFormOverride,
  defaultValues,
  resourceConfiguration,
  initialAttendees,
}: IJxtBookingFormContent) => {
  // @TODO : see when react-hook-form properly supports react-compiler
  // https://github.com/orgs/react-hook-form/discussions/12524
  // https://jooxter.atlassian.net/browse/ND-6018
  'use no memo';
  const { t, i18n } = useTranslation();
  const { user } = useFetchUser();
  const { permissions } = usePermissions();
  const [attendees, setAttendees] = useState<JxtAttendeeOption[]>(initialAttendees);
  const [pendingSubmitData, setPendingSubmitData] = useState<IBookingForm>();
  const { submit, alert, clearAlert, onError, isSubmitting, setIsSubmitting, modals } = useBookingSubmission(
    bookingToEdit,
    onSuccess
  );
  const { invalidateWorkplaceQueries } = useInvalidateWorkplaceQueries();
  const { invalidateBookingQueries } = useInvalidateBookingQueries();

  // The schema depends on values the form itself holds (the dates drive the resource availability,
  // which caps the number of seats), so it can only be built *after* useForm. The resolver reads it
  // through a ref to close that loop without mirroring anything into state.
  const schemaRef = useRef<z.ZodTypeAny | undefined>(undefined);
  const form = useForm<IBookingForm>({
    resolver: (values, context, options) => zodResolver(schemaRef.current as z.ZodTypeAny)(values, context, options),
    defaultValues,
    // Late-arriving defaults (the resource options are fetched separately) re-sync the form through
    // react-hook-form itself instead of a reset() effect.
    values: defaultValues as IBookingForm,
    // ...but that re-sync must not revert what the user already typed: without this, everything
    // entered before the resource options resolved was silently rolled back to the defaults - and
    // the fields lost their dirty state along with it.
    resetOptions: { keepDirtyValues: true },
    mode: 'all',
  });
  const { handleSubmit, control, formState, getValues, trigger } = form;
  const { isValid } = formState;

  const watchSummary = useWatch({ name: 'summary', control });
  const watchStart = useWatch({ name: 'start', control });
  const watchEnd = useWatch({ name: 'end', control });
  const watchResource = useWatch({ name: 'resource', control });
  const watchPhysicalAttendees = useWatch({ name: 'physicalAttendees', control });
  const watchInternalAttendees = useWatch({ name: 'internalAttendees', control });
  const watchExternalAttendees = useWatch({ name: 'externalAttendees', control });
  const watchOrganizerId = useWatch({ name: 'organizerId', control });
  const { user: organizer } = useFetchUserById(watchOrganizerId);

  // Moving the start past the end leaves the range inverted until the effect below corrects the end.
  // Every request keyed on that range is rejected by the API ("End instant must be equal or after
  // start instant"), so nothing is fetched for that transient state.
  const isRangeValid = watchStart.date.isValid && watchEnd.date.isValid && watchStart.date <= watchEnd.date;

  const { status: resourceState } = useFetchResourceStatus(
    {
      resourceId: watchResource.id,
      start: watchStart.date,
      end: watchEnd.date,
    },
    isRangeValid
  );
  const { minPhysicalAttendees, maxPhysicalAttendees } = useMinMaxAttendees(
    resourceConfiguration,
    resourceState,
    bookingToEdit
  );
  const schema = useBookingSchema(minPhysicalAttendees, maxPhysicalAttendees, resourceConfiguration, defaultValues);

  schemaRef.current = useMemo(() => {
    const withZoneCapacityRule = schema.superRefine((data, ctx: z.RefinementCtx) => {
      if (
        resourceConfiguration?.isZone &&
        data.physicalAttendees > minPhysicalAttendees &&
        data.physicalAttendees < data.internalAttendees.length + data.externalAttendees.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t<string>('capacity-attendees.mismatch.error-react', {
            requestedSeats: data.physicalAttendees,
            attendees: data.internalAttendees.length + data.externalAttendees.length - data.physicalAttendees,
          }),
          path: ['internalAttendees'],
        });
      }

      if (
        !resourceConfiguration?.isZone &&
        resourceConfiguration?.guestsRequired &&
        data.internalAttendees.length + data.externalAttendees.length <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t<string>('booking-form.error.physical-attendees.min'),
          path: ['internalAttendees'],
        });
      }
    });

    return withZoneCapacityRule
      .superRefine(
        dateNotInPastSuperRefinerFactory(
          'booking-form.error.date-not-in-past',
          resourceConfiguration?.timezone,
          !!bookingToEdit
        )
      )
      .superRefine(startBeforeEndSuperRefinerFactory('booking-form.error.compare-date'));
  }, [schema, bookingToEdit, minPhysicalAttendees, resourceConfiguration, t]);

  // Two quota systems run in parallel: the legacy corporation-wide daily quota, and the
  // user-group quota per resource type and period (ND-6198). Each yields its own alert.
  const { corporation } = useFetchCorporationById(organizer?.corporation?.id);
  const { quota: corporationQuota } = useFetchCorporationQuota(
    corporation?.id,
    watchStart.date.toFormat('yyyy-MM-dd'),
    !!corporation?.daily_quota
  );
  const dailyQuotaAlert = getDailyQuota({
    resourceType: resourceConfiguration?.metaType,
    dailyQuota: corporation?.daily_quota,
    remainingTime: corporationQuota?.remaining_time,
    language: i18n.language,
  });

  const { quota: resourceQuota } = useFetchUserResourceQuota(
    watchOrganizerId,
    watchResource.id,
    toISO(watchStart.date),
    toISO(watchEnd.date),
    !!watchResource.id && isRangeValid
  );
  const resourceQuotaAlert = getResourceQuotaAlert({ quota: resourceQuota, language: i18n.language });

  // Submission is blocked when the requested duration doesn't fit in either quota. When editing,
  // the booking's current duration is already counted against both, so it is credited back: only
  // extending it beyond what was already booked can overflow (the server applies the same delta
  // rule, see ND-6199). The legacy quota reports seconds, the resource quota minutes.
  const requestedMinutes = isRangeValid ? watchEnd.date.diff(watchStart.date, 'minutes').minutes : 0;
  const creditedMinutes = bookingToEdit
    ? fromISO(bookingToEdit.end.dateTime).diff(fromISO(bookingToEdit.start.dateTime), 'minutes').minutes
    : 0;
  const corporationRemainingMinutes =
    corporationQuota?.remaining_time === undefined ? undefined : Number(corporationQuota.remaining_time) / 60;
  const isQuotaExhausted =
    (!!dailyQuotaAlert &&
      overflowsQuota({ remainingMinutes: corporationRemainingMinutes, requestedMinutes, creditedMinutes })) ||
    (!!resourceQuotaAlert &&
      overflowsQuota({ remainingMinutes: resourceQuota?.remainingTime, requestedMinutes, creditedMinutes }));

  const bookingMetaType = useMemo(() => {
    const metaType = resourceConfiguration?.metaType;

    return metaType === ResourceTypeDtoMetaTypeEnum.Desk || metaType === ResourceTypeDtoMetaTypeEnum.Parking
      ? metaType
      : undefined;
  }, [resourceConfiguration?.metaType]);

  const { user: overlapOrganizer } = useFetchUserById(watchOrganizerId);
  const { overlappingBookings, isLoading: isOverlapLoading } = useCheckBookingOverlap(
    watchOrganizerId,
    bookingMetaType,
    isRangeValid ? { from: watchStart.date, to: watchEnd.date } : undefined,
    !permissions.allowBookingOverlap,
    bookingToEdit?.id
  );

  const { openOverlapResolution, overlapResolutionModal } = useOverlapResolution(
    overlappingBookings ?? [],
    watchResource,
    async (commit) => {
      try {
        await commit();
      } catch (error) {
        onError(error);
        setIsSubmitting(false);
        return;
      }
      invalidateBookingQueries((overlappingBookings ?? []).map((booking) => booking.id));
      invalidateWorkplaceQueries();
      if (pendingSubmitData) {
        submit(pendingSubmitData);
      }
    },
    (booking, resource) => {
      if (pendingSubmitData) {
        onMoveToNewResource?.(booking, resource, { formData: { ...pendingSubmitData, resource }, attendees });
      }
    },
    () => setIsSubmitting(false)
  );

  // Desks and parkings are subject to workplace strategy limits; the check only matters when the
  // booked days actually change.
  const shouldCheckRemainingBookings = useMemo(() => {
    if (!bookingMetaType || !isRangeValid) {
      return false;
    }

    if (!bookingToEdit) {
      return true;
    }

    const oldStart = fromISO(bookingToEdit.start.dateTime, bookingToEdit.resource.timezone).startOf('day');
    const oldEnd = fromISO(bookingToEdit.end.dateTime, bookingToEdit.resource.timezone).startOf('day');

    return !(watchStart.date.startOf('day').equals(oldStart) && watchEnd.date.startOf('day').equals(oldEnd));
  }, [bookingToEdit, watchStart, watchEnd, bookingMetaType, isRangeValid]);

  const { data: remainingBookingsCheck, isError: isRemainingBookingsError } = useFetchRemainingBookingsCheck(
    {
      locationId: watchResource.locationId,
      from: watchStart.date,
      to: watchEnd.date,
      ownerId: watchOrganizerId,
    },
    shouldCheckRemainingBookings
  );
  // On error we still let the user book rather than locking the form on a failed check.
  const hasRemainingBookings = isRemainingBookingsError
    ? true
    : remainingBookingsCheck?.every((item) => item.remainingDays > 0);

  useKeepBookingDuration(form, defaultValues);

  // The zod resolver validates the whole object, but react-hook-form only assigns the resulting
  // errors to the field that changed. Fields whose validity depends on *other* fields - or on the
  // schema being rebuilt - have to be re-validated explicitly.
  useEffect(() => {
    trigger(['summary', 'physicalAttendees', 'internalAttendees']);
  }, [
    trigger,
    schema,
    watchSummary,
    watchPhysicalAttendees,
    watchInternalAttendees,
    watchExternalAttendees,
    minPhysicalAttendees,
    maxPhysicalAttendees,
  ]);

  const onSubmit = (data: IBookingForm) => {
    createGTMGAEvent(
      'Booking',
      bookingToEdit ? 'Modify' : 'Create Booking',
      bookingToEdit ? 'Modify booking' : 'Validation'
    );
    clearAlert();

    if (!bookingToEdit && overlappingBookings && overlappingBookings.length > 0) {
      setPendingSubmitData(data);
      openOverlapResolution({ from: data.start.date, to: data.end.date });
      return;
    }

    submit(data);
  };

  // Both of these commit without a second click: the user has already confirmed, either by picking
  // an instantly bookable slot or by confirming the overlap-resolution "move" modal.
  const hasAutoSubmitted = useRef(false);
  const latest = useLatest({ submit, onSubmit, getValues });

  useEffect(() => {
    if (hasAutoSubmitted.current) {
      return;
    }

    if (moveFormOverride) {
      hasAutoSubmitted.current = true;
      latest.current.submit(moveFormOverride.formData);
      return;
    }

    if (slotToBookInstantly && isValid) {
      hasAutoSubmitted.current = true;
      latest.current.onSubmit(latest.current.getValues());
    }
  }, [moveFormOverride, slotToBookInstantly, isValid, latest]);

  const isBusy = isSubmitting || isOverlapLoading;

  return (
    <FormProvider<IBookingForm> {...form}>
      <form
        key="form"
        className="w-full h-full flex flex-col justify-between overflow-hidden -mt-1 -ml-1"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-3 pb-6 px-1 pt-1 overflow-auto grow">
          {dailyQuotaAlert && (
            <JxtAlert
              type={dailyQuotaAlert.type}
              text={t('booking-form.alert.remaining-time', {
                remainingTime: dailyQuotaAlert.formattedRemainingTime,
                period: UserQuotaRulePeriodEnum.Day,
              })}
            />
          )}
          {resourceQuotaAlert && (
            <JxtAlert
              type={resourceQuotaAlert.type}
              text={t('booking-form.alert.remaining-time', {
                remainingTime: resourceQuotaAlert.formattedRemainingTime,
                period: resourceQuotaAlert.period,
              })}
            />
          )}
          {overlappingBookings && overlappingBookings.length > 0 && (
            <JxtAlert
              type={JxtAlertTypeEnum.Warning}
              text={t('booking-form.conflicting-desk-or-parking-booking', {
                bookingMetaType,
                self: watchOrganizerId === user?.id,
                firstname: overlapOrganizer?.firstname,
                lastname: overlapOrganizer?.lastname,
              })}
            />
          )}
          {alert && <JxtAlert type={JxtAlertTypeEnum.Error} text={alert} closeable />}
          {bookingMetaType && remainingBookingsCheck && (
            <JxtWorkplaceStrategyLimitContainer
              workplaceType={WorkplaceRuleDtoWorkplaceTypeEnum.Office as unknown as JxtWorkplaceTypeWithoutUnknownEnum}
              locationId={watchResource.locationId}
              organizerId={watchOrganizerId}
              remainingBookings={remainingBookingsCheck}
              isBooking={true}
            />
          )}
          <JxtBookingFormFields
            schema={schema}
            minPhysicalAttendees={minPhysicalAttendees}
            maxPhysicalAttendees={maxPhysicalAttendees}
            resourceConfiguration={resourceConfiguration}
            resourceState={resourceState}
            bookingToEdit={bookingToEdit}
            attendees={attendees}
            setAttendees={setAttendees}
          />
        </div>
        <footer className="pt-3 pb-3 max-sm:pb-4 pl-1 border-t border-neutral-20">
          <JxtButton
            variant={isBusy ? ButtonVariantEnum.OutlinePrimary : ButtonVariantEnum.Primary}
            className="w-full"
            type="submit"
            disabled={
              isBusy || !isValid || (hasRemainingBookings === false && shouldCheckRemainingBookings) || isQuotaExhausted
            }
          >
            {isBusy
              ? t('submit-btn-pending')
              : bookingToEdit
                ? t('booking-form.submit-edit')
                : t('booking-form.submit-create')}
          </JxtButton>
        </footer>
      </form>
      {modals}
      {overlapResolutionModal}
    </FormProvider>
  );
};

export default JxtBookingFormContent;
